import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { apiFetch } from '../utils/api';

/**
 * Ignored-senders triage queue (learning loop, move 1).
 *
 * Every sender the allowed-list gate silently drops, with evidence:
 * volume, sample subjects, fuzzy client match, optional AI peek.
 * Decisions: allow (effective immediately, no deploy) / notification-only /
 * link to an existing client.
 */
function SendersTriagePage() {
  const [state, setState] = useState({ status: 'loading', senders: [], error: null });
  const [busy, setBusy] = useState(null); // sender_email being acted on
  const [showClassified, setShowClassified] = useState(false);
  const [message, setMessage] = useState(null);

  const load = useCallback(async () => {
    try {
      const res = await apiFetch('/api/senders/triage');
      if (!res.ok) throw new Error('Eroare la încărcarea expeditorilor');
      const data = await res.json();
      setState({ status: 'success', senders: data.senders || [], error: null });
    } catch (e) {
      setState({ status: 'error', senders: [], error: e.message });
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const classify = async (sender, classification, linkedClientId = null) => {
    setBusy(sender.sender_email);
    setMessage(null);
    try {
      const res = await apiFetch('/api/senders/classify', {
        method: 'POST',
        body: JSON.stringify({
          sender_email: sender.sender_email,
          classification,
          linked_client_id: linkedClientId,
        }),
      });
      const d = await res.json();
      if (!res.ok) throw new Error(d?.msg || 'Eroare la clasificare');
      setMessage({
        type: 'success',
        text: classification === 'allowed'
          ? `${sender.sender_email} este acum permis${d.linked ? ` și legat de ${d.linked.client_name}` : ''} — emailurile din ultimele 48h se procesează la următoarea verificare.`
          : `${sender.sender_email} marcat ca doar-notificări (ignorat permanent, documentat).`,
      });
      await load();
    } catch (e) {
      setMessage({ type: 'error', text: e.message });
    } finally {
      setBusy(null);
    }
  };

  const peek = async (sender) => {
    setBusy(sender.sender_email);
    setMessage(null);
    try {
      const res = await apiFetch('/api/senders/peek', {
        method: 'POST',
        body: JSON.stringify({ sender_email: sender.sender_email }),
      });
      const d = await res.json();
      if (!res.ok) throw new Error(d?.msg || 'Evaluarea AI a eșuat');
      await load();
    } catch (e) {
      setMessage({ type: 'error', text: e.message });
    } finally {
      setBusy(null);
    }
  };

  const visible = state.senders.filter((s) => showClassified || !s.classification);

  return (
    <div className="page-container">
      <main>
        <div className="section-card">
          <div className="section-header">
            <h2 className="section-title">Expeditori ignorați — triaj</h2>
            <p className="section-subtitle">
              Emailuri respinse de filtrul de expeditori. Permite, clasifică drept doar-notificări
              sau leagă de un emitent existent. Deciziile au efect imediat, fără deploy.
            </p>
            <div style={{ display: 'flex', gap: '1rem', alignItems: 'center' }}>
              <Link to="/emails" className="review-btn">← Înapoi la Emailuri</Link>
              <label style={{ fontSize: '0.85rem', opacity: 0.8 }}>
                <input
                  type="checkbox"
                  checked={showClassified}
                  onChange={(e) => setShowClassified(e.target.checked)}
                  style={{ marginRight: '0.4rem' }}
                />
                arată și expeditorii deja clasificați
              </label>
            </div>
          </div>
          <div className="section-content">
            {message && (
              <div className={message.type === 'error' ? 'error-message' : 'success-message'}
                   style={{ marginBottom: '1rem' }}>
                {message.text}
              </div>
            )}
            {state.status === 'loading' && 'Se încarcă…'}
            {state.status === 'error' && <div style={{ color: '#ff6b6b' }}>{state.error}</div>}
            {state.status === 'success' && visible.length === 0 && (
              <div style={{ opacity: 0.75 }}>Coada de triaj este goală. 🎉</div>
            )}
            {state.status === 'success' && visible.map((s) => (
              <div key={s.sender_email} className="section-card"
                   style={{ marginBottom: '0.8rem', padding: '1rem 1.2rem' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', flexWrap: 'wrap', gap: '0.5rem' }}>
                  <div>
                    <strong style={{ fontFamily: 'monospace' }}>{s.sender_email}</strong>
                    {s.sender_name ? <span style={{ opacity: 0.7 }}> — {s.sender_name}</span> : null}
                    <span style={{ marginLeft: '0.8rem', opacity: 0.7 }}>
                      {s.count} email(uri), ultimul {s.last_seen ? new Date(s.last_seen).toLocaleDateString('ro-RO') : '—'}
                    </span>
                    {s.classification && (
                      <span style={{ marginLeft: '0.8rem', padding: '0.1rem 0.6rem', borderRadius: 12,
                                     background: 'rgba(211,192,132,0.12)', fontSize: '0.78rem' }}>
                        {s.classification === 'allowed' ? 'permis' :
                         s.classification === 'notification_only' ? 'doar notificări' : s.classification}
                      </span>
                    )}
                  </div>
                  <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
                    <button className="review-btn" disabled={busy === s.sender_email}
                            onClick={() => peek(s)}>
                      {busy === s.sender_email ? '…' : 'Evaluează cu AI'}
                    </button>
                    <button className="review-btn" disabled={busy === s.sender_email}
                            onClick={() => classify(s, 'allowed',
                              s.suggested_client ? s.suggested_client.client_id : null)}>
                      Permite{s.suggested_client ? ` + leagă de ${s.suggested_client.client_name.slice(0, 22)}…` : ''}
                    </button>
                    {s.suggested_client && (
                      <button className="review-btn" disabled={busy === s.sender_email}
                              onClick={() => classify(s, 'allowed', null)}>
                        Permite fără legare
                      </button>
                    )}
                    <button className="review-btn" disabled={busy === s.sender_email}
                            onClick={() => classify(s, 'notification_only')}>
                      Doar notificări
                    </button>
                  </div>
                </div>
                <div style={{ marginTop: '0.5rem', fontSize: '0.85rem', opacity: 0.8 }}>
                  {s.sample_subjects && s.sample_subjects.length > 0 && (
                    <div>Subiecte recente: {s.sample_subjects.filter(Boolean).map((x) => `„${x}”`).join(' · ')}</div>
                  )}
                  {s.suggested_client && (
                    <div>Potrivire probabilă: <strong>{s.suggested_client.client_name}</strong>
                      {' '}<span style={{ opacity: 0.6 }}>(#{s.suggested_client.client_id}, cuvinte: {s.suggested_client.matched_tokens.join(', ')})</span>
                    </div>
                  )}
                  {s.llm_peek && (
                    <div style={{ marginTop: '0.3rem' }}>
                      <strong>AI:</strong> [{s.llm_peek.pattern}] {s.llm_peek.summary}
                      {' '}— {s.llm_peek.recommend_allow ? 'recomandă permiterea' : 'NU recomandă permiterea'}
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      </main>
    </div>
  );
}

export default SendersTriagePage;
